import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useContracts, PropertyContract } from '@/hooks/useContracts';
import ContractsTable from '@/components/v2/organisms/admin/tabs/ContractsTab/ContractsTable';
import ContractFormModal from '@/components/v2/organisms/admin/tabs/ContractsTab/ContractFormModal';
import ContractsFilters from './ContractsFilters';

export interface PropertiesContractsViewRef {
  createNew: () => void;
}

const PropertiesContractsView = forwardRef<PropertiesContractsViewRef>((_, ref) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContract, setEditingContract] = useState<PropertyContract | null>(null);
  const { data: contracts, isLoading } = useContracts();

  // Filter states
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);
  const [adminType, setAdminType] = useState<string>('all');

  useImperativeHandle(ref, () => ({
    createNew: () => setIsModalOpen(true),
  }));

  const handleEdit = (contract: PropertyContract) => {
    setEditingContract(contract);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingContract(null);
  };

  // Filter contracts based on selected filters
  const filteredContracts = useMemo(() => {
    return (contracts || []).filter((contract) => {
      // Filter by organization
      const matchesOrg =
        organizationIds.length === 0 ||
        organizationIds.includes(contract.organization_id || '');

      // Filter by property
      const matchesProperty =
        propertyIds.length === 0 || propertyIds.includes(contract.property_id);

      // Filter by admin type
      const matchesAdminType =
        adminType === 'all' || contract.admin_type === adminType;

      return matchesOrg && matchesProperty && matchesAdminType;
    });
  }, [contracts, organizationIds, propertyIds, adminType]);

  return (
    <div className="space-y-6">
      <ContractsFilters
        organizationIds={organizationIds}
        onOrganizationIdsChange={setOrganizationIds}
        propertyIds={propertyIds}
        onPropertyIdsChange={setPropertyIds}
        adminType={adminType}
        onAdminTypeChange={setAdminType}
      />

      <ContractsTable
        contracts={filteredContracts}
        isLoading={isLoading}
        onEdit={handleEdit}
      />

      <ContractFormModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        contract={editingContract}
      />
    </div>
  );
});

PropertiesContractsView.displayName = 'PropertiesContractsView';

export default PropertiesContractsView;
